/**
 * Deferred Block Loader
 * Handles lazy loading of heavy blocks like hero-callout for better performance
 */

class DeferredBlockLoader {
    constructor() {
        this.blocks = document.querySelectorAll('[data-defer-loading="true"]');
        this.loadedBlocks = new Set();
        this.observer = null;

        this.init();
    }

    init() {
        if (this.blocks.length === 0) {
            return;
        }

        // Set up intersection observer for lazy loading
        this.setupIntersectionObserver();

        // Also load blocks that are already in viewport
        this.loadVisibleBlocks();
    }

    setupIntersectionObserver() {
        const options = {
            root: null,
            rootMargin: '50px', // Start loading 50px before the block comes into view
            threshold: 0.1
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.loadedBlocks.has(entry.target)) {
                    this.loadBlock(entry.target);
                }
            });
        }, options);

        // Observe all deferred blocks
        this.blocks.forEach(block => {
            this.observer.observe(block);
        });
    }

    loadVisibleBlocks() {
        this.blocks.forEach(block => {
            const rect = block.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

            if (isVisible && !this.loadedBlocks.has(block)) {
                this.loadBlock(block);
            }
        });
    }

    async loadBlock(block) {
        if (this.loadedBlocks.has(block)) {
            return;
        }

        this.loadedBlocks.add(block);

        const blockType = block.dataset.blockType;
        const loadingIndicator = block.querySelector('.hero-callout-loading');
        const placeholder = block.querySelector('.hero-callout-placeholder');
        const actualContent = block.querySelector('.hero-callout');

        // Show loading indicator
        if (loadingIndicator) {
            loadingIndicator.style.display = 'block';
        }

        try {
            // Load the block's assets
            await this.loadBlockAssets(blockType);

            // Show the actual content with a smooth transition
            if (actualContent) {
                actualContent.classList.remove('hero-callout--hidden');
                actualContent.style.opacity = '0';
                actualContent.style.transition = 'opacity 0.5s ease-in-out';

                // Trigger reflow
                actualContent.offsetHeight;

                // Fade in
                actualContent.style.opacity = '1';
            }

            // Hide placeholder and loading indicator
            if (placeholder) {
                placeholder.style.transition = 'opacity 0.3s ease-out';
                placeholder.style.opacity = '0';
                setTimeout(() => {
                    placeholder.style.display = 'none';
                }, 300);
            }

            if (loadingIndicator) {
                loadingIndicator.style.display = 'none';
            }

            // Initialize block-specific functionality
            this.initializeBlock(block, blockType);

        } catch (error) {
            console.error('Error loading deferred block:', error);

            // Fallback: show content anyway
            if (actualContent) {
                actualContent.classList.remove('hero-callout--hidden');
            }
            if (placeholder) {
                placeholder.style.display = 'none';
            }
            if (loadingIndicator) {
                loadingIndicator.style.display = 'none';
            }
        }
    }

    async loadBlockAssets(blockType) {
        const promises = [];

        // Load CSS if not already loaded
        if (blockType === 'hero-callout') {
            promises.push(this.loadCSS('ldc-hero-callout-styles'));
        } else if (blockType === 'stats') {
            promises.push(this.loadCSS('ldc-stats-styles'));
        }

        // Load JavaScript if not already loaded (runtime first for slider blocks)
        if (blockType === 'hero-callout') {
            promises.push(this.loadJS('ldc-tiny-slider-runtime').then(() => this.loadJS('ldc-hero-callout-scripts')));
        } else if (blockType === 'stats') {
            promises.push(this.loadJS('ldc-tiny-slider-runtime').then(() => this.loadJS('ldc-stats-scripts')));
        }

        await Promise.all(promises);
    }

    loadCSS(handle) {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (document.querySelector(`link[href*="${handle}"]`)) {
                resolve();
                return;
            }

            // Get the CSS URL from WordPress
            const cssUrl = this.getAssetUrl(handle, 'style');
            if (!cssUrl) {
                resolve(); // Skip if URL not found
                return;
            }

            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssUrl;
            link.onload = () => resolve();
            link.onerror = () => reject(new Error(`Failed to load CSS: ${cssUrl}`));

            document.head.appendChild(link);
        });
    }

    loadJS(handle) {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (document.querySelector(`script[src*="${handle}"]`)) {
                resolve();
                return;
            }

            // Get the JS URL from WordPress
            const jsUrl = this.getAssetUrl(handle, 'script');
            if (!jsUrl) {
                resolve(); // Skip if URL not found
                return;
            }

            const script = document.createElement('script');
            script.src = jsUrl;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load JS: ${jsUrl}`));

            document.head.appendChild(script);
        });
    }

    getAssetUrl(handle, type) {
        const themeUrl = window.ldcTheme?.url || '/wp-content/themes/ldc-2023';
        const version = window.ldcTheme?.version || '1.0.0';

        if (type === 'style') {
            if (handle === 'ldc-hero-callout-styles') {
                const v = window.ldcTheme?.heroCalloutStyleVersion || version;
                return `${themeUrl}/dist/styles/blocks/_hero-callout.css?v=${v}`;
            } else if (handle === 'ldc-stats-styles') {
                return `${themeUrl}/dist/styles/blocks/_stats.css?v=${version}`;
            }
        } else if (type === 'script') {
            if (handle === 'ldc-tiny-slider-runtime') {
                if (window.ldcTheme?.deferredTinySliderUrl) {
                    return window.ldcTheme.deferredTinySliderUrl
                }
                return `${themeUrl}/dist/scripts/vendor/tiny-slider-runtime.js?ver=${encodeURIComponent(version)}`
            }
            if (handle === 'ldc-hero-callout-scripts') {
                if (window.ldcTheme?.deferredHeroCalloutSliderUrl) {
                    return window.ldcTheme.deferredHeroCalloutSliderUrl
                }
                const v = window.ldcTheme?.heroCalloutScriptVersion || version
                return `${themeUrl}/dist/scripts/blocks/hero-callout-slider.js?ver=${encodeURIComponent(v)}`
            }
            if (handle === 'ldc-stats-scripts') {
                if (window.ldcTheme?.deferredStatsSliderUrl) {
                    return window.ldcTheme.deferredStatsSliderUrl
                }
                return `${themeUrl}/dist/scripts/blocks/stats.js?ver=${encodeURIComponent(version)}`
            }
        }

        return null;
    }

    initializeBlock(block, blockType) {
        // Initialize block-specific functionality
        if (blockType === 'hero-callout') {
            // The hero-callout slider will initialize automatically
            // when the script loads, but we can trigger it manually if needed
            setTimeout(() => {
                // Trigger any custom initialization if needed
                const event = new CustomEvent('hero-callout-loaded', {
                    detail: { block: block }
                });
                document.dispatchEvent(event);
            }, 100);
        } else if (blockType === 'stats') {
            // The stats slider will initialize automatically
            // when the script loads, but we can trigger it manually if needed
            setTimeout(() => {
                // Trigger any custom initialization if needed
                const event = new CustomEvent('stats-loaded', {
                    detail: { block: block }
                });
                document.dispatchEvent(event);
            }, 100);
        }
    }

    // Method to manually load a specific block (useful for testing)
    loadBlockNow(block) {
        if (block && block.dataset.deferLoading === 'true') {
            this.loadBlock(block);
        }
    }

    // Method to load all remaining blocks
    loadAllBlocks() {
        this.blocks.forEach(block => {
            if (!this.loadedBlocks.has(block)) {
                this.loadBlock(block);
            }
        });
    }

    // Cleanup method
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.deferredBlockLoader = new DeferredBlockLoader();
});

// Export for potential external use
export default DeferredBlockLoader;
